import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { captureApolloError } from "services/external/sentry";
import type { Fridge } from "types";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";

type RecoverArgs = {
	hasNoActiveSalesPoint: boolean;
	/**
	 * The provider's shouldSkipSubscription. When false the live stream is (or will be) up and the
	 * broken-subscription reconnect path owns recovery — firing here too would double-pull the
	 * initial state, so this hook only acts while the subscription is startup-skipped.
	 */
	isSubscriptionSkipped: boolean;
	fridgeId: string;
	refetch: () => Promise<{ data?: { fridge?: Fridge | null } }>;
	onData: (fridge: Fridge) => void;
};

/**
 * Recovers a fridge that BOOTED with a CONFLICT (no active sales point) error (JID-2257).
 *
 * On such a boot the startup gate (resolveShouldSkipSubscription) skips the live subscription and
 * nothing ever re-runs INITIAL_STATE_QUERY — the kiosk is stranded on the error screen even after
 * the sales point is activated. The hourly price refetch DOES clear hasNoActiveSalesPoint when the
 * backend recovers, so this hook watches for that true→false edge while the subscription is still
 * skipped and re-pulls the initial state once. The fresh, error-free response flips hasValidFridge,
 * the startup latch opens, and the subscription connects through the normal boot flow — no special
 * restart machinery needed.
 *
 * Same defensive idioms as useReconnectAfterBrokenSubscription:
 * - prevRef edge detection, so mounting with the flag in either position never fires on its own.
 * - onData/refetch are kept current via refs, so a provider re-render storm (fresh arrow identities
 *   each render) can neither re-trigger the effect nor fire a stale closure.
 * - Single-flight guard: a flag flap while a refetch is still in the air piggybacks on that flight
 *   instead of double-firing the query; the guard releases at every terminal (success, empty, throw).
 */
export const useRecoverFromNoActiveSalesPoint = ({
	hasNoActiveSalesPoint,
	isSubscriptionSkipped,
	fridgeId,
	refetch,
	onData,
}: RecoverArgs): void => {
	const onDataRef = useRef(onData);
	const refetchRef = useRef(refetch);
	// Tracks the previous flag so the effect below can detect the true→false clearing edge.
	const prevHasNoActiveSalesPointRef = useRef(hasNoActiveSalesPoint);
	const refetchInFlightRef = useRef(false);

	useEffect(() => {
		onDataRef.current = onData;
	}, [onData]);

	useEffect(() => {
		refetchRef.current = refetch;
	}, [refetch]);

	useEffect(() => {
		const salesPointRecovered = prevHasNoActiveSalesPointRef.current && !hasNoActiveSalesPoint;
		prevHasNoActiveSalesPointRef.current = hasNoActiveSalesPoint;
		if (!(salesPointRecovered && isSubscriptionSkipped && fridgeId)) {
			return;
		}
		if (refetchInFlightRef.current) {
			return;
		}
		refetchInFlightRef.current = true;
		const runRecoveryRefetch = async (): Promise<void> => {
			try {
				logToSentry(`Sending initial query after sales point recovered on fridge: ${fridgeId}`, "warning");
				const res = await refetchRef.current();
				if (res.data?.fridge) {
					onDataRef.current(res.data.fridge);
				} else {
					captureApolloError(
						new Error("Initial query error"),
						{
							kind: "Initial_Query_Error",
							query: INITIAL_STATE_QUERY.kind,
							variables: { fridgeId: fridgeId },
						},
						{
							ResponseData: res,
						},
					);
				}
			} catch (err) {
				Sentry.captureException(err);
			} finally {
				refetchInFlightRef.current = false;
			}
		};
		runRecoveryRefetch();
	}, [hasNoActiveSalesPoint, isSubscriptionSkipped, fridgeId]);
};
